<template>
  <div>
    <v-dialog v-model="inputDialog" persistent max-width="600px">
      <v-card :elevation="2" class="pa-3">
        <v-card-title class="headline">Upload File</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="formValid">
            <v-text-field v-model="plantName" readonly outlined required label="Plant Name"></v-text-field>
            <v-select
              v-model="selectedYear"
              :items="yearItems"
              label="PVSyst File Generated Year"
              item-value="value"
              item-text="text"
              outlined
              required
            ></v-select>
            <v-text-field
              v-if="selectedYear"
              v-model="selectedFileName"
              label="Choose file"
              placeholder="Select a file"
              required
              outlined
              :rules="[v => !v || /(\.xlsx|\.xls)$/i.test(v) || 'Only Excel files (.xlsx, .xls) are allowed']"
              append-icon="mdi-paperclip"
              @click="triggerFileInput"
              @click:append="triggerFileInput"
              readonly
              class="file-input"
            ></v-text-field>
            <v-select
              v-model="commissionYear"
              :items="yearItems"
              label="Commission Year"
              item-value="value"
              item-text="text"
              outlined
              required
            ></v-select>
            <v-text-field
              v-model="degradationFactor"
              label="Degradation Factor"
              type="number"
              outlined
              :rules="[v => !v || (!isNaN(parseFloat(v)) && parseFloat(v) >= 0.3 && parseFloat(v) <= 0.75) || 'Degradation factor must be between 0.3 and 0.75']"
              hint="Enter a decimal between 0.3 and 0.75"
              persistent-hint
              placeholder="0.3"
             ></v-text-field>
            <v-select
              v-model="degradationAppliedYear"
              :items="yearItems"
              label="Degradation Applied Year"
              item-value="value"
              item-text="text"
              outlined
              required
            ></v-select>
            <input
              v-if="selectedYear"
              type="file"
              ref="fileInput"
              accept=".xlsx, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              @change="onFileChange"
              class="d-none"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog" class="cancel-btn">Cancel</v-btn>
          <v-btn class="upload-btn" :disabled="!formValid || !selectedFile" @click="previewDialog = true">Upload</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="previewDialog" persistent max-width="450px">
      <v-card class="pa-3">
        <v-card-title class="headline">
          <v-icon color="primary" left>mdi-cloud-upload-outline</v-icon>
          Confirm Upload
        </v-card-title>
        <v-card-text class="py-3 body-1">
              <div>
            Would you like to compute expectations immediately upon upload, or just upload the file and keep it inactive?
          </div> 
        </v-card-text>
        <v-card-actions>
           <v-spacer></v-spacer> 
         <v-btn text @click="previewDialog = false" class="cancel-btn">Cancel</v-btn>
          <v-btn class="upload-btn mx-1" @click="confirmUpload(false)">Upload</v-btn>
          <v-btn  class="upload-btn mx-1"  @click="confirmUpload(true)">Upload & Compute</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Confirmation dialog before triggering computation -->
    <v-dialog v-model="computeConfirmDialog" persistent max-width="400px">
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="primary">mdi-play-circle-outline</v-icon>
          Confirm Computation
        </v-card-title>
        <v-card-text class="body-1 pt-2">
          <div class="mb-2">Are you sure you want to compute for this file?</div>
          <div class="confirm-file-box mb-3">
            <div class="caption grey--text">File name</div>
            <div class="body-2 font-weight-medium">{{ computeFileName }}</div>
          </div>
          <div class="caption grey--text mt-2">
            This will compute expected energy from commission year
            <strong>{{ pendingCompute ? pendingCompute.commissionYear : '' }}</strong>
            up to the current year.
          </div>
          <div class="mt-3">
            <div class="caption font-weight-medium mb-1">Type yes to confirm:</div>
            <v-text-field
              v-model="confirmComputeText"
              outlined 
              dense
              autofocus
              hide-details
              placeholder="yes"
              class="mt-1"
            ></v-text-field>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text class="cancel-btn" @click="computeConfirmDialog = false; pendingCompute = null; confirmComputeText = ''">Cancel</v-btn>
          <v-btn class="upload-btn" @click="confirmCompute" :disabled="!confirmComputeText || confirmComputeText.trim().toLowerCase() !== 'yes'">
            Compute
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-layout row wrap justify-space-between>
      <v-flex xs12>
        <v-toolbar color="primary">
          <v-toolbar-title>Files</v-toolbar-title>
          <v-spacer></v-spacer>

          <v-btn icon @click="downloadTemplate" title="Download Template" class="white--text mx-1 toolbar-btn">
            <v-icon>mdi-cloud-download</v-icon>
          </v-btn>
          <v-btn icon @click="openInputHandler" title="Upload a File" class="white--text mx-1 toolbar-btn">
            <v-icon>mdi-file-upload</v-icon>
          </v-btn>
        </v-toolbar>
        <v-data-table
          :headers="tableHeaders"
          :items="tableData"
          class="elevation-1 custom-table"
          hide-actions
          :loading="false"
        >
          <!-- Clickable "Select" header to toggle radio button visibility -->
          <template v-slot:headers="props">
            <tr>
              <th
                v-for="header in props.headers"
                :key="header.value"
                :class="['column', header.align === 'left' ? 'text-xs-left' : 'text-xs-center']"
                :style="{ width: header.width }"
              >
                <span>
                  {{ header.text }}
                </span>
              </th>
            </tr>
          </template>

          <template v-slot:items="props">
            <tr :class="{ 'active-row': props.item.active }">
              <td class="text-xs-center py-1 px-4 select-col">
                <v-progress-circular
                  v-if="settingActiveRow === props.item.fileName"
                  indeterminate
                  size="20"
                  width="2"
                  color="primary"
                ></v-progress-circular>
                <v-icon
                  v-else
                  @click="selectRowRadio(props.item)"
                  class="radio-icon"
                  :class="{ 'primary--text': tempSelectedFile === props.item.fileName }"
                  title="Select this file"
                >
                  {{ tempSelectedFile === props.item.fileName ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}
                </v-icon>
              </td>
              <td class="text-xs-left py-1 px-2 user-col">{{ props.item.uploadedBy }}</td>
              <td class="text-xs-center py-1 px-2 date-col">{{ props.item.uploadedAt }}</td>
              <td class="text-xs-center py-1 px-2 filename-col">{{ props.item.fileName }}</td>
              <td class="text-xs-center py-1 px-2 commission-col">{{ props.item.commissionYear }}</td>
              <td class="text-xs-center py-1 px-2 degradation-col">{{ props.item.degradationFactor }}</td>
              <td class="text-xs-center py-1 px-2 applied-year-col">{{ props.item.degradationAppliedYear }}</td>
              <td class="text-xs-center py-1 px-2 year-col">{{ props.item.year }}</td>
              <td class="text-xs-center py-1 actions-col">
                  <v-btn
                    icon
                    @click="downloadFile(props.item)"
                    class="dow-file"
                    title="Download File"
                  >
                    <v-icon>mdi-download</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="tempSelectedFile === props.item.fileName"
                    icon
                    :loading="settingActiveRow === props.item.fileName"
                    :disabled="settingActiveRow !== null"
                    @click="initiateCompute(props.item)"
                    class="trigger-module-btn"
                    title="Run computation for this file"
                    color="primary"
                  >
                    <v-icon>mdi-play-circle</v-icon>
                  </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>
  </div>
</template>
    
  <script>
import moment from "moment";
import { mapGetters, mapMutations } from "vuex";
import pvsystApi from "../services/pvsyst";
import ExcelJS from "exceljs";

export default {
  data() {
    return {
      inputDialog: false,
      previewDialog: false,
      selectedYear: "",
      selectedFile: null,
      selectedFileName: "",
      commissionYear: "",
      degradationFactor: "",
      degradationAppliedYear: "",
      formValid: false,
      yearItems: [],
      tableHeaders: [
        {
          text: "",
          value: "select",
          align: "center",
          sortable: false,
          width: "5%"
        },
        {
          text: "Uploaded by",
          value: "uploadedBy",
          align: "left",
          sortable: false,
          width: "12%"
        },
        {
          text: "Uploaded on",
          value: "uploadedAt",
          align: "center",
          sortable: false,
          width: "10%"
        },
        {
          text: "File Name",
          value: "fileName",
          align: "center",
          sortable: false,
          width: "25%"
        },
        {
          text: "Commission Year",
          value: "commissionYear",
          align: "center",
          sortable: false,
          width: "8%"
        },
        {
          text: "Degradation Factor",
          value: "degradationFactor",
          align: "center",
          sortable: false,
          width: "8%"
        },
        {
          text: "Degradation Applied Year",
          value: "degradationAppliedYear",
          align: "center",
          sortable: false,
          width: "10%"
        },
        {
          text: "Pvsyst Generated Year",
          value: "year",
          align: "center",
          sortable: false,
          width: "8%"
        },
        {
          text: "Actions",
          value: "actions",
          align: "center",
          sortable: false,
          width: "10%"
        }
      ],
      tableData: [],
      settingActiveRow: null,
      tempSelectedFile: null,
      computeConfirmDialog: false,
      pendingCompute: null,
      confirmComputeText: ""
    };
  },
  computed: {
    ...mapGetters(["getSelectedPlantId", "getPlantName", "getActivePlantInfo", "getUserInfo"]),
    computeFileName() {
      if (!this.pendingCompute) return '';
      return this.pendingCompute.fileName || '';
    },
    plantName() {
      return this.getPlantName(this.getSelectedPlantId);
    },
    plantCommissionYear() {
      const commissionDate = this.getActivePlantInfo('commission_date');
      if (!commissionDate) return null;
      const parsed = moment(commissionDate, ['YYYY-MM-DD', 'DD-MM-YYYY', 'YYYY/MM/DD'], true);
      return parsed.isValid() ? parsed.year() : null;
    },
    userInfo() {
      return this.getUserInfo('email') || this.getUserInfo('userName') || 'N/A';
    }
  },
  async mounted() {
    this.generateYearItems();
    await this.loadTableData();
  },
  methods: {
    ...mapMutations([
      "SET_PROGRESS_BAR",
      "SET_SHOW_SNACKBAR",
      "SET_SNACKBAR_CONTENT"
    ]),
    updateSnackbar(payload) {
      this.SET_SHOW_SNACKBAR(true);
      this.SET_SNACKBAR_CONTENT(payload);
    },
    async loadTableData() {
      try {
        const response = await pvsystApi.loadTableData(this.getSelectedPlantId);
        const sortedData = response.data.data
          .map(row => ({
            year: row.year,
            uploadedBy: row.updatedBy,
            uploadedAt: new Date(row.updatedAt),
            fileName: row.filename.split("/").pop(),
            commissionYear: row.commissionYear || moment().year(),
            degradationFactor: row.degradationFactor || '0.3',
            degradationAppliedYear: row.degradationAppliedYear  || moment().year(),
            active: row.active
          }))
          .sort((a, b) => b.uploadedAt - a.uploadedAt);
        this.tableData = sortedData.map(row => ({
          ...row,
          uploadedAt: row.uploadedAt.toLocaleString()
        }));
      } catch (error) {
        console.error("Error loading table data:", error);
      }
    },
    generateYearItems() {
      const currentYear = new Date().getFullYear();
      for (let i = 0; i < 15; i++) {
        const year = currentYear - i;
        this.yearItems.push({ value: year, text: year.toString() });
      }
    },
    onFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.selectedFile = file;
        this.selectedFileName = file.name;
      }
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    closeDialog() {
      this.inputDialog = false;
      this.resetForm();
    },
    async confirmUpload(shouldCompute) {
      this.previewDialog = false;
      this.inputDialog = false;
      
      try {
        const formData = new FormData();
        formData.append("plantId", this.getSelectedPlantId);
        formData.append("year", this.selectedYear);
        formData.append("commissionYear", this.commissionYear);
        formData.append("degradationFactor", this.degradationFactor);
        formData.append("degradationAppliedYear", this.degradationAppliedYear);
        formData.append("file", this.selectedFile);
        const response = await pvsystApi.submitForm(
          this.getSelectedPlantId,
          this.selectedYear,
          formData,
        );
        if (response.status === 200) {
          this.updateSnackbar({
            icon: "success",
            timeout: 5000,
            header: "",
            message: "File has been uploaded successfully!"
          });

          const fileName = (response.data && response.data.fileName) || this.selectedFileName;

          const uploadMeta = {
            fileName,
            commissionYear: this.commissionYear,
            degradationFactor: this.degradationFactor,
            degradationAppliedYear: this.degradationAppliedYear,
            year: this.selectedYear
          };

          this.resetForm();

          if (shouldCompute) {
            await this.triggerActiveComputation(uploadMeta);
          } else {
            await this.loadTableData();
          }
        }
      } catch (error) {
        this.updateSnackbar({
          icon: "error",
          timeout: 5000,
          message: "Error uploading file"
        });
      }
    },
    openInputHandler() {
      this.inputDialog = true;
    },
    resetForm() {
      this.selectedYear = "";
      this.selectedFile = null;
      this.selectedFileName = "";
      this.commissionYear = "";
      this.degradationFactor = "";
      this.degradationAppliedYear = "";
      this.formValid = false;
    },
    async downloadFile(row) {
      const fileName = row.fileName;
      try {
        const response = await pvsystApi.getPresignedUrl(
          this.getSelectedPlantId,
          fileName
        );
        if (response && response.data && response.data.url) {
          const presignedUrl = response.data.url;
          window.open(presignedUrl);
        } else {
          throw new Error("No URL returned from the server");
        }
      } catch (error) {
        console.error("Error fetching presigned URL:", error);
      }
    },
    initiateCompute(row) {
      this.pendingCompute = row;
      this.confirmComputeText = "";
      this.computeConfirmDialog = true;
    },
    confirmCompute() {
      const confirmationText = (this.confirmComputeText || "").trim().toLowerCase();
      if (confirmationText !== "yes") {
        this.updateSnackbar({
          icon: "error",
          timeout: 4000,
          header: "",
          message: 'Please type "yes" to confirm computation.'
        });
        return;
      }

      this.computeConfirmDialog = false;
      if (this.pendingCompute) {
        const row = this.pendingCompute;
        this.pendingCompute = null;
        this.confirmComputeText = "";
        this.triggerActiveComputation(row);
      } else {
        this.confirmComputeText = "";
      }
    },
    selectRowRadio(row) {
      // Toggle selection: clicking the same row again deselects it
      this.tempSelectedFile = this.tempSelectedFile === row.fileName ? null : row.fileName;
    },
    async triggerActiveComputation(row) {
      this.settingActiveRow = row.fileName;
      try {
        const response = await pvsystApi.setActiveFile(
          this.getSelectedPlantId,
          row.fileName,
          row.commissionYear,
          row.degradationFactor,
          row.degradationAppliedYear,
          row.year,
          row.uploadedBy
        );
        this.updateSnackbar({
          icon: "success",
          timeout: 7000,
          header: "",
          message: response.data.message || `File set to active and computation triggered!`
        });
        this.tempSelectedFile = null;
        await this.loadTableData();
      } catch (error) {
        console.error("Error triggering computation:", error);
        this.updateSnackbar({
          icon: "error",
          timeout: 5000,
          header: "",
          message: "Failed to trigger computation. Please try again."
        });
      } finally {
        this.settingActiveRow = null;
      }
    },
  async downloadTemplate() {
    try {
      const response = await pvsystApi.downloadTemplate();
      if (response && response.data && response.data.url) {
        const presignedUrl = response.data.url;
        window.open(presignedUrl, '_blank');
      } else {
        throw new Error("No URL returned from the server");
      }
      } catch (error) {
        this.updateSnackbar({
          icon: "error",
          timeout: 5000,
          header: "",
          message: "Failed to download template"
        });
      }
   }
  },
  watch: {
    getSelectedPlantId() {
      this.loadTableData();
    }
  }
};
</script>

  <style scoped>
.v-dialog .v-card,
.v-dialog .v-card-title,
.v-dialog .v-card-actions {
  background-color: var(--v-primary-base) !important;
  color: var(--primarytext);
}

.file-input {
  width: 100%;
}

.theme--dark.v-btn.cancel-btn {
  background-color: #ff6b7e;
  color: white;
  margin-bottom: 10px;
  margin-right: 10px;
}

.theme--dark.v-btn.upload-btn {
  background-color: #4caf50;
  color: white;
  margin-bottom: 10px;
}

.spreadsheet-container {
  width: 100%;
  height: 500px;
  margin-top: 20px;
}

.headline {
  margin-left: 30px;
}

.v-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 30px;
}
.select-col { width: 6%; }
.user-col { width: 12%; }
.date-col { width: 10%; }
.filename-col { width: 25%; }
.commission-col { width: 8%; }
.degradation-col { width: 8%; }
.applied-year-col { width: 10%; }
.year-col { width: 8%; }
.actions-col { width: 10%; }
.clickable-icon { cursor: pointer; }
</style>
 
  <style>
.custom-table tbody tr.active-row,
.custom-table tbody tr.active-row td,
.v-table tbody tr.active-row,
.v-table tbody tr.active-row td,
table.v-table tbody tr.active-row,
table.v-table tbody tr.active-row td {
  background-color: rgba(76, 175, 80, 0.18) !important;
}

.theme--dark .custom-table tbody tr.active-row,
.theme--dark .custom-table tbody tr.active-row td,
.theme--dark .v-table tbody tr.active-row,
.theme--dark .v-table tbody tr.active-row td,
.theme--dark table.v-table tbody tr.active-row,
.theme--dark table.v-table tbody tr.active-row td {
  background-color: rgba(76, 175, 80, 0.32) !important;
}

.custom-table tbody tr.active-row td:first-child,
.v-table tbody tr.active-row td:first-child,
table.v-table tbody tr.active-row td:first-child {
  border-left: 6px solid #4caf50 !important;
}

.custom-table tbody tr.active-row td,
.v-table tbody tr.active-row td {
  font-weight: 600 !important;
}

.custom-table thead th:first-child {
  padding-left: 24px !important;
}

.custom-table thead tr:first-child {
  height: 54px !important;
}

.custom-data-table th {
  z-index: 1;
  position: sticky;
  top: 0;
}
.menu-button {
  background-color: var(--v-primary-base) !important;
  color: var(--primarytext);
}

/* Theme handling for download & upload icons */
.theme--light .dow-file .v-icon,
.theme--light .trigger-module-btn .v-icon {
  color: rgba(0, 0, 0, 0.7) !important;
}
.theme--dark .dow-file .v-icon,
.theme--dark .trigger-module-btn .v-icon {
  color: rgba(255, 255, 255, 0.85) !important;
}
.dow-file:hover .v-icon,
.trigger-module-btn:hover .v-icon {
  color: var(--v-info-base) !important;
}

.theme--light .toolbar-btn .v-icon {
  color: rgba(0, 0, 0, 0.7) !important;
}
.theme--dark .toolbar-btn .v-icon {
  color: #ffffff !important;
}
.toolbar-btn:hover .v-icon {
  color: var(--v-accent-base) !important;
}

/* Radio Button Theme Styles */
.radio-icon {
  cursor: pointer;
}
.theme--light .radio-icon:not(.primary--text) {
  color: rgba(0, 0, 0, 0.6) !important;
}
.theme--dark .radio-icon:not(.primary--text) {
  color: #ffffff !important;
}
.radio-icon.primary--text,
.theme--dark .radio-icon.primary--text,
.theme--light .radio-icon.primary--text {
  color: #2196f3 !important;
}
</style>